import { BalanceSheetV1 } from "../../grader/balance-sheet/v1.grader";
import { AnswerConfig } from "../../grader/grade-input";
import { ExtractInput } from "../extract-input";
import { ExtractOutput } from "../extract-output";
import { extractTable } from "../utils/extract-table";
import { extractValueAndConfigFromCell } from "../utils/extract-value-from-cell";
import { getAccountAndLevel } from "../utils/get-account-and-level";

/**
 * extractBalanceSheetV1
 *
 * 'table.balance-sheet' 테이블에서 학생이 작성한
 * 대차대조표 데이터를 추출하여 BalanceSheetV1[] 형태로 가공해 반환합니다.
 *
 * 📌 사용 목적:
 * - 학생의 제출 데이터를 구조화하여 채점, 비교에 사용
 *
 * @param extractInput - ExtractInput (version, questionType, div 포함)
 * @returns ExtractOutput<BalanceSheetV1[]> 형태의 가공된 데이터
 */
export function extractBalanceSheetV1(
  extractInput: ExtractInput,
): ExtractOutput<BalanceSheetV1[]> {
  const { version, questionType, div } = extractInput;

  /**
   * 1️⃣ 'table.balance-sheet' 테이블을 추출
   * - 없을 경우 에러를 발생시켜 사용자에게 테이블 작성 요청
   */
  const table = extractTable(div, `table.balance-sheet`);
  if (!table) throw new Error("테이블을 만들어주세요.");

  /**
   * 2️⃣ 데이터 시작 행 인덱스 설정
   * - 보통 앞의 2줄은 제목/헤더이므로 데이터는 3번째 줄(sliceStart=2)부터 시작
   */
  const sliceStart = 2;
  const flatAccountSheets: BalanceSheetV1[] = [];

  /**
   * 3️⃣ 테이블의 모든 <tr>을 가져와 데이터 영역만 순회
   */
  const trs = table.getElementsByTagName("tr");
  for (const tr of Array.from(trs).slice(sliceStart)) {
    const tds = tr.getElementsByTagName("td");

    /**
     * 4️⃣ 해당 행에서 계정명과 계정 레벨 추출
     * - getAccountAndLevel: select에서 계정명과 'depth' 클래스로 레벨 파싱
     */
    const [name, level] = getAccountAndLevel(tr);

    /**
     * 5️⃣ 해당 행의 각 열 데이터를 추출하여 values 배열에 저장
     * - 첫 번째 열은 계정명이므로 제외 (slice(1))
     */
    const values: string[] = [];
    const answerConfigs: (AnswerConfig | undefined)[] = [];
    Array.from(tds)
      .slice(1)
      .forEach((td) => {
        const { value, answerConfig } = extractValueAndConfigFromCell(td);
        values.push(value);
        answerConfigs.push(answerConfig);
      });

    /**
     * 6️⃣ BalanceSheetV1 형태로 구조화하여 배열에 저장
     */
    flatAccountSheets.push({
      id: tr.id,
      name,
      values,
      level,
      answerConfigs,
    });
  }

  /**
   * 7️⃣ 최종 추출된 데이터를 ExtractOutput 형태로 반환
   */
  return {
    version, // 문제 버전
    questionType, // 문제 유형
    entryCount: flatAccountSheets.length, // 추출된 행 수
    answerSheet: flatAccountSheets, // 추출된 데이터
  };
}
